import { useDarkMode } from '@/hooks/useDarkMode';
import { ExternalLink, MapPin, Sparkles } from 'lucide-react';
import { getArchetypeWorld } from '@/config/experienceInspirations';
import BrandWorld from '@/components/browse/BrandWorld';
import {
  getArchetypeAccent,
  getArchetypeTextAccent,
} from '@/config/archetypes';

/**
 * ArchetypeWorld — the outbound-affiliate content shown inside any non-Curated
 * archetype (Experiences, Handmade, DIY & Kits, ...).
 *
 * When the archetype's slug has NO matching world config in experienceInspirations
 * (e.g. `support_a_brand` today, or any admin-created archetype), we render a
 * "Coming Soon" placeholder instead — respecting the archetype's font + accent
 * so the world still feels branded.
 */
export default function ArchetypeWorld({ archetype }) {
  const darkMode = useDarkMode();
  const isDark = darkMode.isDark;
  const world = getArchetypeWorld(archetype?.slug);
  const accent = getArchetypeAccent(archetype, isDark);
  const textAccent = getArchetypeTextAccent(archetype, isDark);

  if (!archetype) return null;

  // Support a Brand has its own live directory-driven world.
  if (archetype.slug === 'support_a_brand') {
    return (
      <BrandWorld archetype={archetype} accent={accent} textAccent={textAccent} isDark={isDark} />
    );
  }

  // Coming Soon fallback — used for archetypes with no world content yet.
  if (!world) {
    return (
      <div
        data-testid={`archetype-world-${archetype.slug}-coming-soon`}
        className="rounded-2xl p-8 sm:p-14 text-center"
        style={{
          background: isDark
            ? `linear-gradient(135deg, ${accent}30 0%, rgba(45,67,84,0.6) 100%)`
            : `linear-gradient(135deg, ${accent}25 0%, rgba(255,250,245,0.95) 100%)`,
          border: `1px solid ${accent}55`,
        }}
      >
        <div className="max-w-2xl mx-auto">
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold mb-4 px-3 py-1 rounded-full"
            style={{
              color: textAccent,
              background: `${accent}22`,
              border: `1px solid ${accent}55`,
            }}
          >
            <Sparkles size={12} />
            Coming Soon
          </div>
          <h2
            className="mb-3"
            style={{
              fontFamily: archetype.fontFamily,
              ...archetype.titleStyle,
              fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
              lineHeight: 1.1,
              color: isDark ? '#FFFAF5' : '#20212B',
            }}
          >
            {archetype.title} is on its way
          </h2>
          <p
            className="text-sm sm:text-base mb-6"
            style={{
              color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)',
              fontFamily: 'Playfair Display, serif',
            }}
          >
            {archetype.tagline || 'We are curating this world right now — check back soon.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`archetype-world-${archetype.slug}`}>
      {/* --- Affiliate Portal Banners --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {world.portals.map((portal) => (
          <a
            key={portal.id}
            href={portal.outbound_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid={`archetype-portal-${archetype.slug}-${portal.id}`}
            className="group relative overflow-hidden rounded-2xl transition-all hover:shadow-lg"
            style={{
              padding: '28px 24px',
              background: isDark
                ? `linear-gradient(135deg, ${accent}55 0%, rgba(45,67,84,0.95) 100%)`
                : `linear-gradient(135deg, ${accent}55 0%, #FFFAF5 100%)`,
              border: `1.5px solid ${accent}88`,
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div
                  className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2"
                  style={{ color: isDark ? accent : textAccent }}
                >
                  {archetype.slug === 'experiences' ? 'Experience Partner' : 'Affiliate Partner'}
                </div>
                <div
                  className="mb-1"
                  style={{
                    fontFamily: archetype.fontFamily,
                    ...archetype.titleStyle,
                    fontSize: '28px',
                    lineHeight: 1.1,
                    color: isDark ? '#FFFAF5' : '#20212B',
                  }}
                >
                  {portal.name}
                </div>
                <p
                  className="text-sm"
                  style={{
                    color: isDark
                      ? 'rgba(255,250,245,0.85)'
                      : 'rgba(83,65,69,0.9)',
                  }}
                >
                  {portal.tagline}
                </p>
              </div>
              <ExternalLink
                className="flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                style={{ color: isDark ? accent : textAccent }}
                size={22}
              />
            </div>
          </a>
        ))}
      </div>

      {/* --- Inspiration section title --- */}
      <div className="mb-5">
        <div
          className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2"
          style={{ color: isDark ? accent : textAccent }}
        >
          Inspiration
        </div>
        <h2
          className="mb-1"
          style={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 600,
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            color: isDark ? '#FFFAF5' : '#20212B',
          }}
        >
          {world.inspirationHeading}
        </h2>
        <p
          className="text-sm"
          style={{
            color: isDark ? 'rgba(255,250,245,0.7)' : 'rgba(83,65,69,0.8)',
          }}
        >
          {world.inspirationSubheading}
        </p>
      </div>

      {/* --- Inspiration tiles grid --- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
        {world.inspirations.map((item) => (
          <a
            key={item.id}
            href={item.outbound_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid={`archetype-inspiration-${archetype.slug}-${item.id}`}
            className="group block relative overflow-hidden rounded-xl transition-all hover:shadow-md"
            style={{
              aspectRatio: '4 / 5',
              background: isDark ? '#1E2A36' : '#F5F5F5',
              border: `1px solid ${
                isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
              }`,
            }}
          >
            <img
              src={item.image}
              alt={item.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Bottom gradient overlay */}
            <div
              className="absolute inset-x-0 bottom-0 pointer-events-none"
              style={{
                height: '60%',
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 55%, transparent 100%)',
              }}
            />
            <div className="relative h-full flex flex-col justify-end p-3 sm:p-4">
              <h3
                className="text-white mb-0.5"
                style={{
                  fontFamily: archetype.fontFamily,
                  ...archetype.titleStyle,
                  fontSize: '15px',
                  lineHeight: 1.15,
                }}
              >
                {item.title}
              </h3>
              <p className="text-white/80 text-xs leading-snug">
                {item.description}
              </p>
            </div>
          </a>
        ))}
      </div>

      {/* --- Closing CTA --- */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        style={{
          background: isDark
            ? `linear-gradient(135deg, ${accent}40 0%, rgba(45,67,84,0.6) 100%)`
            : `linear-gradient(135deg, ${accent}40 0%, rgba(255,250,245,0.9) 100%)`,
          border: `1px solid ${accent}66`,
        }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} style={{ color: isDark ? accent : textAccent }} />
            <span
              className="text-[10px] uppercase tracking-[0.18em] font-bold"
              style={{ color: isDark ? accent : textAccent }}
            >
              {world.closingCta.eyebrow}
            </span>
          </div>
          <h3
            className="mb-1"
            style={{
              fontFamily: archetype.fontFamily,
              ...archetype.titleStyle,
              fontSize: '22px',
              color: isDark ? '#FFFAF5' : '#20212B',
            }}
          >
            {world.closingCta.title}
          </h3>
          <p
            className="text-sm max-w-md"
            style={{
              color: isDark
                ? 'rgba(255,250,245,0.75)'
                : 'rgba(83,65,69,0.85)',
            }}
          >
            {world.closingCta.body}
          </p>
        </div>
        <a
          href={world.portals[0]?.outbound_url || '#'}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`archetype-cta-${archetype.slug}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-transform hover:scale-105"
          style={{
            background: accent,
            color: '#FFFAF5',
          }}
        >
          {world.closingCta.button}
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}
